import { useState, useEffect, useCallback, useRef } from 'react';
import { findIndex, flow, get, isEmpty, isEqual } from 'lodash';
import { updateArrayIndexFp } from 'Utils/immutability/immutability';
import { IS_VALID, IS_INVALID, NOT_VALIDATED } from './validationStates';

function _isFormValid(fields: Array<FormField>): boolean {
  return fields.every((field) => field.validationState === IS_VALID);
}

function _isFieldValid(field: FormField): boolean {
  const fieldValidators = get(field, 'validators') || [];
  return !isEmpty(fieldValidators)
    ? fieldValidators.every((validator) => validator.isValid(field.value))
    : true;
}

function _setFieldValidationState(field: FormField): FormField {
  if (isEmpty(get(field, 'validators'))) {
    return { ...field, validationState: IS_VALID };
  }

  if (!field.value) {
    return { ...field, validationState: _isFieldValid(field) ? IS_VALID : NOT_VALIDATED };
  }

  return { ...field, validationState: _isFieldValid(field) ? IS_VALID : IS_INVALID };
}

type GetFieldsFn = (props: any) => Array<FormField>;
type CallBack = (arg0: any) => any;

export function useFormValidator(getFields: GetFieldsFn, props: any) {
  const initialFields = (getFields(props) || []).map(_setFieldValidationState);
  const [fields, setFields] = useState<Array<FormField>>(initialFields);
  const prevPropsRef = useRef(props);

  useEffect(() => {
    if (!isEqual(props, prevPropsRef.current)) {
      prevPropsRef.current = props;
      const formFields = (getFields(props) || []).map(_setFieldValidationState);
      setFields(formFields);
    }
  });

  const setFieldsInState = useCallback(
    (currentProps: any) => {
      const formFields = (getFields(currentProps) || []).map(_setFieldValidationState);
      setFields(formFields);
    },
    [getFields],
  );

  const validateFields = useCallback(
    (callback?: CallBack): Array<FormField> => {
      const fieldsWithValidation = fields.map(_setFieldValidationState);
      setFields(fieldsWithValidation);
      if (callback) {
        callback(fieldsWithValidation);
      }
      return fieldsWithValidation;
    },
    [fields],
  );

  const updateFieldValue = useCallback(
    (
      fieldName: string,
      options?: { validate: boolean },
      callback?: CallBack,
    ): ((arg0: any) => void) => {
      return (newFieldValue: any) => {
        setFields((currentFields) => {
          const indexToUpdate = findIndex(currentFields, { name: fieldName });
          const newFields = updateArrayIndexFp(currentFields, indexToUpdate, (field) => ({
            ...field,
            value: newFieldValue,
          }));

          if (get(options, 'validate')) {
            const validated = newFields.map(_setFieldValidationState);
            if (callback) {
              callback(validated);
            }
            return validated;
          }

          return newFields;
        });
      };
    },
    [],
  );

  const updateFieldsValue = useCallback(
    (
      keyValues: Array<{ fieldName: string; value: any }>,
      options?: { validate: boolean },
      callback?: CallBack,
    ) => {
      setFields((currentFields) => {
        const newFields = keyValues.reduce((updatedFields, { fieldName, value }) => {
          const indexToUpdate = findIndex(updatedFields, { name: fieldName });
          return updateArrayIndexFp(updatedFields, indexToUpdate, (field) => ({
            ...field,
            name: fieldName,
            value,
          }));
        }, currentFields);

        if (get(options, 'validate')) {
          const validated = newFields.map(_setFieldValidationState);
          if (callback) {
            callback(validated);
          }
          return validated;
        }

        return newFields;
      });
    },
    [],
  );

  const isFormSubmittable = useCallback((): boolean => {
    return flow(() => {
      const fieldsWithValidation = fields.map(_setFieldValidationState);
      setFields(fieldsWithValidation);
      return fieldsWithValidation;
    }, _isFormValid)();
  }, [fields]);

  const isFieldValid = useCallback((field: FormField): boolean => {
    return _isFieldValid(field);
  }, []);

  const isFormValid = useCallback((): boolean => {
    return _isFormValid(fields);
  }, [fields]);

  const onSubmit = useCallback(
    (onSubmitProp?: (fields: Array<FormField>) => any): boolean | null | undefined => {
      return isFormSubmittable() && onSubmitProp?.(fields);
    },
    [fields, isFormSubmittable],
  );

  const getFieldByName = useCallback(
    (fieldName: string): FormField | null | undefined => {
      return fields.find((field) => field.name === fieldName);
    },
    [fields],
  );

  const getFieldValueByName = useCallback(
    (fieldName: string): any => {
      const formField = fields.find((field) => field.name === fieldName);
      return get(formField, 'value');
    },
    [fields],
  );

  return {
    fields,
    setFields,
    validateFields,
    updateFieldValue,
    updateFieldsValue,
    isFormSubmittable,
    isFieldValid,
    isFormValid,
    onSubmit,
    getFieldByName,
    getFieldValueByName,
    setFieldsInState,
  };
}
